'use client';

import { FC, useEffect } from 'react';
import { X } from 'lucide-react';
import type { Announcement } from '@/services/announcements';
import { getAnnouncementTypeConfig } from './typeConfig';
import AnnouncementVideo from './AnnouncementVideo';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';

interface AnnouncementVideoLightboxProps {
  announcement: Announcement | null;
  onClose: () => void;
}

/**
 * Full-screen video lightbox, opened from any placement (strip/toast/panel). Rendered once
 * (single instance) and driven by the AnnouncementsProvider. Escape / backdrop click closes.
 */
const AnnouncementVideoLightbox: FC<AnnouncementVideoLightboxProps> = ({ announcement, onClose }) => {
  useEffect(() => {
    if (!announcement) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [announcement, onClose]);

  if (!announcement || !announcement.video_url) return null;
  const c = getAnnouncementTypeConfig(announcement.type);
  const Icon = c.icon;

  return (
    <Dialog open onOpenChange={(next) => { if (!next) onClose(); }}>
      {/* p-0 so the video runs edge to edge; the standard 80% cap and scroll
          still apply, and the caption below the player scrolls if it needs to. */}
      <DialogContent
        className="max-w-[880px] gap-0 overflow-hidden rounded-2xl p-0"
        showCloseButton={false}
        aria-label={announcement.title}
      >
        <DialogTitle className="sr-only">{announcement.title}</DialogTitle>
        <button
          type="button"
          aria-label="Close video"
          onClick={onClose}
          className="absolute right-3 top-3 z-30 flex h-9 w-9 items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70"
        >
          <X className="h-4 w-4" strokeWidth={2.2} />
        </button>
        <AnnouncementVideo videoUrl={announcement.video_url} posterUrl={announcement.poster_url} autoStart className="rounded-none border-0" />
        <div className="flex items-start gap-3 px-5 py-4">
          <div className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border bg-white ${c.tintBorder} ${c.accentText}`}>
            <Icon className="h-4 w-4" strokeWidth={2.1} />
          </div>
          <div className="min-w-0">
            <div className="text-[15px] font-semibold text-slate-900">{announcement.title}</div>
            {announcement.body && <div className="mt-0.5 text-[13px] text-slate-600">{announcement.body}</div>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AnnouncementVideoLightbox;
